import { apiClient } from '@/utils/api';

export type BillingDocType = 'QUOTE' | 'INVOICE' | 'CREDIT_NOTE';
export type BillingStatus =
  | 'DRAFT'
  | 'ISSUED'
  | 'SENT'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'EXPIRED'
  | 'PARTIALLY_PAID'
  | 'PAID'
  | 'CREDITED'
  | 'SUPERSEDED';
export type PriceListUnit = 'CORPO' | 'ORA' | 'CTP';

export type BillingLine = {
  id?: string;
  sortOrder?: number;
  description: string;
  unit: PriceListUnit;
  quantity: number;
  unitPrice: number;
  lineTotal?: number;
  priceListItemId?: string | null;
  sourceQuoteLineId?: string | null;
  remainingQty?: number;
};

export type BillingDocument = {
  id: string;
  type: BillingDocType;
  status: BillingStatus;
  numberDisplay: string | null;
  revision: number;
  clientId: string;
  client?: { id: string; displayName: string; email?: string | null };
  practiceId?: string | null;
  practice?: { id: string; title: string; practiceNumber?: string | null } | null;
  validUntil?: string | null;
  dueDate?: string | null;
  notesPublic?: string | null;
  notesInternal?: string | null;
  pdfPath?: string | null;
  issuedAt?: string | null;
  lines: BillingLine[];
  total: number;
  allocated?: number;
  credited?: number;
  quotes?: Array<{ id: string; numberDisplay: string | null; revision: number }>;
  pdfs?: Array<{ id: string; revision: number; filePath: string }>;
  createdAt: string;
};

export const billingApi = {
  list: (params?: Record<string, string>) =>
    apiClient.get('/billing/documents', { params }).then((r) => r.data.data as BillingDocument[]),
  get: (id: string) => apiClient.get(`/billing/documents/${id}`).then((r) => r.data.data as BillingDocument),
  create: (body: unknown) => apiClient.post('/billing/documents', body).then((r) => r.data.data as BillingDocument),
  update: (id: string, body: unknown) =>
    apiClient.put(`/billing/documents/${id}`, body).then((r) => r.data.data as BillingDocument),
  remove: (id: string) => apiClient.delete(`/billing/documents/${id}`).then((r) => r.data.data),
  emit: (id: string, overrideSeq?: number) =>
    apiClient.post(`/billing/documents/${id}/emit`, { overrideSeq }).then((r) => r.data.data as BillingDocument),
  pdfUrl: (id: string) => `/api/billing/documents/${id}/pdf`,
  openPdf: async (id: string, fallbackName = 'documento.pdf') => {
    try {
      const response = await apiClient.get(`/billing/documents/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data as BlobPart], { type: 'application/pdf' });
      const headers = response.headers as Record<string, string> | undefined;
      const disposition = headers?.['content-disposition'] || headers?.['Content-Disposition'];
      const match = disposition?.match(/filename="?([^"]+)"?/);
      const filename = match?.[1] || fallbackName;
      const url = window.URL.createObjectURL(blob);
      const opened = window.open(url, '_blank');
      if (!opened) {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error: any) {
      const data = error?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        let message = 'Impossibile aprire il PDF';
        try {
          const parsed = JSON.parse(text);
          if (parsed?.message) message = parsed.message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }
      throw error;
    }
  },
  revision: (id: string) =>
    apiClient.post(`/billing/documents/${id}/revision`).then((r) => r.data.data as BillingDocument),
  acceptance: (id: string, status: 'ACCEPTED' | 'REJECTED') =>
    apiClient.post(`/billing/documents/${id}/acceptance`, { status }).then((r) => r.data.data as BillingDocument),
  fromQuotes: (body: unknown) =>
    apiClient.post('/billing/invoices/from-quotes', body).then((r) => r.data.data as BillingDocument),
  creditNote: (body: unknown) =>
    apiClient.post('/billing/credit-notes/from-invoice', body).then((r) => r.data.data as BillingDocument),
  remaining: (ids: string[]) =>
    apiClient.get('/billing/quotes/remaining', { params: { ids: ids.join(',') } }).then((r) => r.data.data),
  sendEmail: (id: string, body: unknown) =>
    apiClient.post(`/billing/documents/${id}/send-email`, body).then((r) => r.data.data as BillingDocument),
  sendWhatsApp: (id: string, body: unknown) =>
    apiClient.post(`/billing/documents/${id}/send-whatsapp`, body).then((r) => r.data.data as BillingDocument),
  priceList: (practiceTypeId?: string) =>
    apiClient.get('/billing/price-list', { params: { practiceTypeId } }).then((r) => r.data.data),
  suggest: (clientId?: string, search?: string, practiceTypeId?: string) =>
    apiClient
      .get('/billing/price-list/suggest', { params: { clientId, search, practiceTypeId } })
      .then((r) => r.data.data as PriceSuggestion[]),
  savePriceItem: (body: unknown) => apiClient.post('/billing/price-list', body).then((r) => r.data.data),
  saveLineToList: (body: unknown) => apiClient.post('/billing/price-list/from-line', body).then((r) => r.data.data),
  createPayment: (body: unknown) => apiClient.post('/billing/payments', body).then((r) => r.data.data),
  updatePayment: (id: string, body: unknown) => apiClient.put(`/billing/payments/${id}`, body).then((r) => r.data.data),
  deletePayment: (id: string) => apiClient.delete(`/billing/payments/${id}`).then((r) => r.data.data),
  allocate: (body: unknown) => apiClient.post('/billing/allocations', body).then((r) => r.data.data),
  deleteAllocation: (id: string) => apiClient.delete(`/billing/allocations/${id}`).then((r) => r.data.data),
  saveOpening: (body: unknown) => apiClient.post('/billing/opening-balances', body).then((r) => r.data.data),
  deleteOpening: (id: string) => apiClient.delete(`/billing/opening-balances/${id}`).then((r) => r.data.data),
  statement: (clientId: string, from?: string, to?: string) =>
    apiClient.get(`/billing/clients/${clientId}/statement`, { params: { from, to } }).then((r) => r.data.data),
  chats: (clientId: string) =>
    apiClient.get(`/billing/clients/${clientId}/whatsapp-chats`).then((r) => r.data.data),
  practiceDocs: (practiceId: string) =>
    apiClient.get(`/billing/practices/${practiceId}/documents`).then((r) => r.data.data as BillingDocument[]),
  aging: () => apiClient.get('/billing/aging').then((r) => r.data.data),
  stats: () => apiClient.get('/billing/stats').then((r) => r.data.data),
  mailboxes: () => apiClient.get('/billing/mailboxes').then((r) => r.data.data),
  settings: () => apiClient.get('/billing/settings').then((r) => r.data.data),
  saveSettings: (body: unknown) => apiClient.put('/billing/settings', body).then((r) => r.data.data),
  setNextNumber: (body: unknown) => apiClient.put('/billing/settings/next-number', body).then((r) => r.data.data),
  uploadLogo: (file: File) => {
    const fd = new FormData();
    fd.append('logo', file);
    return apiClient.post('/billing/settings/logo', fd).then((r) => r.data.data);
  },
};

export function euro(n: number | null | undefined): string {
  return `€${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export const TYPE_LABEL: Record<BillingDocType, string> = {
  QUOTE: 'Preventivo',
  INVOICE: 'Fattura',
  CREDIT_NOTE: 'Nota di credito',
};

export const STATUS_LABEL: Record<BillingStatus, string> = {
  DRAFT: 'Bozza',
  ISSUED: 'Emesso',
  SENT: 'Inviato',
  ACCEPTED: 'Accettato',
  REJECTED: 'Rifiutato',
  EXPIRED: 'Scaduto',
  PARTIALLY_PAID: 'Parz. pagata',
  PAID: 'Saldata',
  CREDITED: 'Stornata',
  SUPERSEDED: 'Sostituito',
};

export const UNIT_LABEL: Record<PriceListUnit, string> = {
  CORPO: 'Corpo',
  ORA: 'Ora',
  CTP: 'CTP',
};

export type ComboboxClient = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  clientType?: string;
  email?: string | null;
};

export function clientDisplayName(c: ComboboxClient): string {
  if (c.clientType === 'COMPANY' && c.companyName) return c.companyName;
  if (c.companyName) return c.companyName;
  const byLast = `${c.lastName || ''} ${c.firstName || ''}`.trim();
  if (byLast) return byLast;
  const byFirst = `${c.firstName || ''} ${c.lastName || ''}`.trim();
  return byFirst || c.email || 'Cliente';
}

export type PriceSuggestion = {
  id: string;
  description: string;
  unit: PriceListUnit;
  unitPrice: number;
  catalogPrice?: number;
  fromClient?: boolean;
  scope?: 'practice' | 'typed' | 'global';
  practiceTypeNames?: string[];
};
